import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ApiClient } from '../../api/client';
import { text } from '../../app/i18n';
import { Icon } from '../../design-system/icons';
import { AppServicesContext } from '../../views/ViewContext';
import { initials } from '../FormLayoutParts';
import type { Entry, Section } from '../form-targets';
import { readLastPerson, writeLastPerson } from './last-person';
import { useMediaRefLinks, refKey } from './media-links';
import { emptyText, presentationOf, type MediaRef } from './presentation';

/**
 * Champ « Personne » des formulaires (ADR-152 T1, inspection-template-body 1.15.0, field-definition 1.9.0) : un
 * champ `reference` dont la cible est une personne de la source déclarée par le gabarit (`person_source` : un rôle
 * de tiers ou un rôle d'employé). Puce avatar + nom + chevron dans un bandeau (`variant: chip`), champ de sélection
 * (`field`) ou bouton compact (`button`) ; le choix se fait dans un menu déroulant ancré sous le contrôle (avatar +
 * nom, coche sur la personne choisie), avec une recherche au-delà de 12 personnes. Les personnes viennent de `ListFormPeople`, qui ne rend que la projection
 * minimale (identifiant, famille, rôle, nom, photo) : le client ne choisit ni le rôle ni la famille, seulement la
 * tâche (`work_item_id`) ou le gabarit (`template`). La réponse reste l'identifiant ; le nom est relu, jamais stocké
 * (ERP-PROJ-002). Présentation seulement : le serveur revalide la personne à la complétion.
 */

export interface FormPerson {
  id: string;
  family: 'party_role' | 'employee_role';
  role: string;
  display_name: string;
  avatar?: MediaRef;
}

export interface FormPeople {
  people: readonly FormPerson[];
  truncated: boolean;
}

export interface TemplateRef {
  artifact_id: string;
  artifact_version?: string;
}

/** D'où la fiche lit ses personnes : la tâche, sinon le gabarit cité ; l'aperçu d'un éditeur montre des exemples. */
export interface FormPeopleSource {
  workItemId?: string | undefined;
  template?: TemplateRef | undefined;
  /** Date civile de la fiche (AAAA-MM-JJ) : le rôle doit y être actif. */
  on?: string | undefined;
  /** Aperçu d'un éditeur : des personnes d'exemple, aucune requête. */
  preview: boolean;
  /** Clé du gabarit pour la « dernière personne choisie » de cet appareil. */
  templateKey: string;
}

export const FormPeopleContext = createContext<FormPeopleSource>({ preview: false, templateKey: '' });

/** Noms repris en sous-titre (`presentation.echo.into`), par identifiant de champ ou de partie. */
export const PersonEchoContext = createContext<ReadonlyMap<string, string>>(new Map());

/** Au-delà de ce nombre de personnes, le menu propose une recherche (ADR-152 §4). */
export const PEOPLE_WITHOUT_SEARCH = 12;

/** Personne d'exemple choisie dans l'aperçu du Studio. */
export const SAMPLE_PERSON_ID = '000000000000000000000a01';
const SAMPLE_NAMES = ['Claire Martin', 'Julien Petit', 'Sophie Bernard', 'Marc Lefèvre', 'Anne Roux'];

function samplePeople(entry: Entry): FormPeople {
  const family = personSourceOf(entry)?.family ?? 'party_role';
  return { people: SAMPLE_NAMES.map((name, index) => ({ id: `000000000000000000000a0${index + 1}`, family, role: 'sample', display_name: name })), truncated: false };
}

type PersonFieldDefinition = { person_source?: { family: 'party_role' | 'employee_role'; roles: string[] }; person_default?: 'site' | 'last' | 'none' };

export function personSourceOf(entry: Entry): PersonFieldDefinition['person_source'] {
  return (entry.field as PersonFieldDefinition).person_source;
}

export function personDefaultOf(entry: Entry): 'site' | 'last' | 'none' {
  return (entry.field as PersonFieldDefinition).person_default ?? 'none';
}

/** Le champ est-il un champ « Personne » ? */
export function isPersonEntry(entry: Entry): boolean {
  return entry.field.type === 'reference' && presentationOf(entry).widget === 'person';
}

export function personVariantOf(entry: Entry): 'chip' | 'field' | 'button' {
  const variant = (presentationOf(entry) as { variant?: string }).variant;
  return variant === 'chip' || variant === 'button' ? variant : 'field';
}

// ---- Lecture des personnes (`ListFormPeople`).

/**
 * Paramètres de `ListFormPeople` (list-form-people-parameters 1.0.0) en chaînes, comme toute requête nommée : la
 * tâche, sinon le gabarit (`template`, objet en JSON) ; jamais le rôle ni la famille.
 */
export function formPeopleParameters(source: FormPeopleSource, fieldId: string, search?: string): Record<string, string> | null {
  const out: Record<string, string> = { field_id: fieldId };
  if (source.workItemId !== undefined) out['work_item_id'] = source.workItemId;
  else if (source.template !== undefined) out['template'] = JSON.stringify(source.template);
  else return null;
  if (source.on !== undefined) out['on'] = source.on;
  if (search !== undefined && search.trim() !== '') out['search'] = search.trim().slice(0, 80);
  return out;
}

const cache = new Map<string, Promise<FormPeople>>();

/** Vide le cache des personnes (tests, changement de session). */
export function clearFormPeople(): void {
  cache.clear();
}

export function loadFormPeople(client: ApiClient, parameters: Record<string, string>): Promise<FormPeople> {
  const key = JSON.stringify(parameters);
  const known = cache.get(key);
  if (known !== undefined) return known;
  const loading = client.query<FormPeople>('ListFormPeople', parameters).then((response) => ({ people: response.data.people ?? [], truncated: response.data.truncated === true }));
  cache.set(key, loading);
  // Un échec ne reste pas en cache : la prochaine ouverture redemande.
  loading.catch(() => cache.delete(key));
  return loading;
}

type PeopleState = { status: 'loading' } | { status: 'ready'; value: FormPeople } | { status: 'error' } | { status: 'none' };

/** Personnes d'un champ ; une recherche serveur quand la liste est tronquée. */
export function useFormPeople(entry: Entry, search?: string): PeopleState {
  const services = useContext(AppServicesContext);
  const source = useContext(FormPeopleContext);
  const fieldId = entry.field.field_id;
  const parameters = source.preview ? null : formPeopleParameters(source, fieldId, search);
  const key = parameters === null ? '' : JSON.stringify(parameters);
  const [state, setState] = useState<PeopleState>(() => (source.preview ? { status: 'ready', value: samplePeople(entry) } : { status: parameters === null || services === null ? 'none' : 'loading' }));
  useEffect(() => {
    if (source.preview) {
      setState({ status: 'ready', value: samplePeople(entry) });
      return;
    }
    if (parameters === null || services === null) {
      setState({ status: 'none' });
      return;
    }
    let alive = true;
    loadFormPeople(services.client, parameters).then(
      (value) => alive && setState({ status: 'ready', value }),
      () => alive && setState({ status: 'error' }),
    );
    return () => {
      alive = false;
    };
    // La clé résume les paramètres.
  }, [services, key, source.preview]);
  return state;
}

function peopleOf(state: PeopleState): readonly FormPerson[] {
  return state.status === 'ready' ? state.value.people : [];
}

/** Adresses des photos des personnes montrées (médiathèque, sous le contrôle d'accès des fichiers). */
function useAvatars(people: readonly FormPerson[]): (person: FormPerson | undefined) => string | undefined {
  const services = useContext(AppServicesContext);
  const refs = useMemo(() => people.map((person) => person.avatar).filter((ref): ref is MediaRef => ref !== undefined), [people]);
  const links = useMediaRefLinks(services?.client, refs);
  return (person) => {
    const key = refKey(person?.avatar);
    return key === undefined ? undefined : links.get(key);
  };
}

/** Comparaison sans casse ni accents. */
const fold = (value: string): string => value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('fr');

export function PersonAvatar({ name, url, size = 34 }: { name: string | undefined; url: string | undefined; size?: number }) {
  return (
    <span className={`ay-fv2-person-avatar${name === undefined ? ' is-empty' : ''}`} style={{ width: size, height: size }} aria-hidden="true">
      {url !== undefined ? <img src={url} alt="" /> : name === undefined ? '?' : initials(name) || '?'}
    </span>
  );
}

/** Place du menu sous son ancre (au-dessus quand la place manque), au moins aussi large qu'elle. */
interface MenuPlace {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

function placeMenu(anchor: HTMLElement): MenuPlace {
  const rect = anchor.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1024;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 768;
  const width = Math.min(Math.max(rect.width, 280), viewportWidth - 16);
  const left = Math.max(8, Math.min(rect.left, viewportWidth - width - 8));
  const below = viewportHeight - rect.bottom - 12;
  const above = rect.top - 12;
  if (below < 240 && above > below) return { bottom: viewportHeight - rect.top + 4, left, width, maxHeight: Math.min(420, above) };
  return { top: rect.bottom + 4, left, width, maxHeight: Math.min(420, Math.max(160, below)) };
}

/**
 * Menu de choix ancré sous la puce ou le champ (retour de l'utilisateur du 2026-10-23 : « presque un combo suffit,
 * avec l'image et le nom ») : une ligne par personne, avatar + nom ; une coche sur la personne choisie, une pastille
 * discrète (« Site », « Dernière ») sur celle proposée au départ ; recherche en tête au-delà de 12 personnes ou sur une
 * liste tronquée ; « Effacer » dans le menu sur un champ facultatif. Fermeture au clic extérieur et par Échap.
 */
export function PersonMenu({
  anchor,
  title,
  entry,
  value,
  marked,
  required,
  onPick,
  onClear,
  onClose,
}: {
  anchor: HTMLElement | null;
  title: string;
  entry: Entry;
  value: string | undefined;
  marked?: { id: string; label: string } | undefined;
  required: boolean;
  onPick: (person: FormPerson) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const all = useFormPeople(entry);
  const truncated = all.status === 'ready' && all.value.truncated;
  // Liste tronquée : la recherche interroge le serveur ; sinon elle filtre la liste reçue.
  const remote = useFormPeople(entry, truncated && query.trim() !== '' ? query : undefined);
  const state = truncated && query.trim() !== '' ? remote : all;
  const people = peopleOf(state);
  const searchable = truncated || peopleOf(all).length > PEOPLE_WITHOUT_SEARCH;
  const shown = truncated || query.trim() === '' ? people : people.filter((person) => fold(person.display_name).includes(fold(query.trim())));
  const avatar = useAvatars(people);
  const menu = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<MenuPlace | null>(() => (anchor === null ? null : placeMenu(anchor)));
  const close = useRef(onClose);
  close.current = onClose;

  useLayoutEffect(() => {
    if (anchor === null) return;
    const update = () => setPlace(placeMenu(anchor));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchor]);

  useEffect(() => {
    const outside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target === null || menu.current?.contains(target) === true || anchor?.contains(target) === true) return;
      close.current();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      close.current();
      anchor?.focus();
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      document.removeEventListener('keydown', escape, true);
    };
  }, [anchor]);

  // Sans recherche, le focus va à la personne choisie (sinon à la première) : le clavier part de là.
  useEffect(() => {
    if (searchable) return;
    const target = menu.current?.querySelector<HTMLElement>('[aria-selected="true"]') ?? menu.current?.querySelector<HTMLElement>('[role="option"]');
    target?.focus({ preventScroll: true });
  }, [searchable, shown.length]);

  const moveFocus = (event: ReactKeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = [...(menu.current?.querySelectorAll<HTMLElement>('[role="option"], .ay-fv2-person-menu__clear') ?? [])];
    if (items.length === 0) return;
    event.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next = event.key === 'ArrowDown' ? (at + 1) % items.length : at <= 0 ? items.length - 1 : at - 1;
    items[next]?.focus();
  };

  const style: CSSProperties = place === null ? {} : { left: place.left, width: place.width, maxHeight: place.maxHeight, ...(place.top === undefined ? { bottom: place.bottom } : { top: place.top }) };
  const body = (
    <div ref={menu} className="ay-fv2-person-menu" role="dialog" aria-label={title} style={style} onKeyDown={moveFocus} data-testid={`person-menu-${entry.field.field_id}`}>
      {searchable && (
        <label className="ay-fv2-person-menu__search">
          <Icon name="search" size={16} />
          <input type="search" value={query} placeholder="Rechercher" aria-label="Rechercher" autoFocus onChange={(event) => setQuery(event.target.value)} />
        </label>
      )}
      {state.status === 'loading' && (
        <p className="ay-fv2-person-menu__note" role="status">
          Chargement…
        </p>
      )}
      {state.status === 'error' && (
        <p className="ay-fv2-person-menu__note" role="alert">
          Liste indisponible.
        </p>
      )}
      {(state.status === 'none' || (state.status === 'ready' && shown.length === 0)) && <p className="ay-fv2-person-menu__note">Aucune personne.</p>}
      {shown.length > 0 && (
        <div className="ay-fv2-person-menu__list" role="listbox" aria-label={title} data-testid={`person-list-${entry.field.field_id}`}>
          {shown.map((person) => {
            const selected = person.id === value;
            return (
              <button key={person.id} type="button" role="option" aria-selected={selected} className={`ay-fv2-person-menu__option${selected ? ' is-selected' : ''}`} onClick={() => onPick(person)}>
                <PersonAvatar name={person.display_name} url={avatar(person)} size={32} />
                <span className="ay-fv2-person-menu__name">{person.display_name}</span>
                {marked?.id === person.id && <span className="ay-fv2-person-menu__mark">{marked.label}</span>}
                <span className="ay-fv2-person-menu__check" aria-hidden="true">
                  {selected && <Icon name="check" size={16} />}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {!required && value !== undefined && (
        <button type="button" className="ay-fv2-person-menu__clear" onClick={onClear}>
          <Icon name="close" size={14} />
          Effacer
        </button>
      )}
    </div>
  );
  // Le menu sort du flux (un bandeau ou une carte ne le coupent pas) mais reste sous la racine du thème (jetons, police).
  return createPortal(body, anchor?.closest<HTMLElement>('.ay-root') ?? document.body);
}

/**
 * Contrôle d'un champ « Personne », dans sa variante : puce (bandeau), champ de sélection ou bouton compact ; un appui
 * ouvre le menu de choix sous le contrôle. Le nom montré est relu de la liste ; une personne absente de la liste reste
 * « Choix enregistré ».
 */
export function PersonControl({ id, entry, value, required, disabled, invalid, onChange, labelledBy }: { id: string; entry: Entry; value: unknown; required: boolean; disabled: boolean; invalid: boolean; onChange: (value: unknown) => void; labelledBy?: string }) {
  const source = useContext(FormPeopleContext);
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const state = useFormPeople(entry);
  const people = peopleOf(state);
  const current = typeof value === 'string' && value !== '' ? value : undefined;
  const person = current === undefined ? undefined : people.find((candidate) => candidate.id === current);
  const avatar = useAvatars(person === undefined ? [] : [person]);
  const name = current === undefined ? undefined : (person?.display_name ?? (state.status === 'loading' ? '…' : 'Choix enregistré'));
  const variant = personVariantOf(entry);
  const label = text(entry.field.label, 'fr', entry.field.field_id);
  const fieldId = entry.field.field_id;
  const last = personDefaultOf(entry) === 'last' && !source.preview ? readLastPerson(source.templateKey, fieldId) : undefined;
  const empty = emptyText(entry);
  const prompt = text((presentationOf(entry) as { placeholder?: Readonly<Record<string, string>> }).placeholder, 'fr', empty.text === '' ? 'Choisir' : empty.text);
  const shown = name ?? prompt;
  const done = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  return (
    <>
      <button
        ref={trigger}
        type="button"
        id={id}
        className={`ay-fv2-person ay-fv2-person--${variant}${name === undefined ? ' is-empty' : ''}${open ? ' is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid}
        {...(labelledBy === undefined ? {} : { 'aria-labelledby': `${labelledBy} ${id}-name` })}
        disabled={disabled}
        data-testid={`person-${fieldId}`}
        onClick={() => setOpen(!open)}
      >
        <PersonAvatar name={name === '…' ? undefined : name} url={avatar(person)} size={variant === 'chip' ? 34 : 32} />
        <span className="ay-fv2-person__name" id={`${id}-name`}>
          {shown}
        </span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} />
      </button>
      {open && (
        <PersonMenu
          anchor={trigger.current}
          title={label}
          entry={entry}
          value={current}
          required={required}
          marked={last === undefined ? undefined : { id: last, label: 'Dernière' }}
          onClose={() => setOpen(false)}
          onClear={() => {
            onChange(null);
            done();
          }}
          onPick={(picked) => {
            onChange(picked.id);
            if (!source.preview) writeLastPerson(source.templateKey, fieldId, picked.id);
            done();
          }}
        />
      )}
    </>
  );
}

// ---- Reprise du nom (`presentation.echo.into`).

interface EchoSource {
  entry: Entry;
  value: string;
  into: readonly string[];
}

/** Champs « Personne » de la fiche (hors listes) remplis et repris ailleurs. */
export function echoSources(sections: readonly Section[], answers: Readonly<Record<string, unknown>>): EchoSource[] {
  const out: EchoSource[] = [];
  for (const section of sections) {
    if (section.series !== undefined || section.repeat !== undefined) continue;
    for (const entry of section.fields) {
      const into = (presentationOf(entry) as { echo?: { into?: readonly string[] } }).echo?.into;
      const value = answers[entry.field.field_id];
      if (isPersonEntry(entry) && into !== undefined && into.length > 0 && typeof value === 'string' && value !== '') out.push({ entry, value, into });
    }
  }
  return out;
}

/** Fournit les noms repris : chaque cible citée reçoit le nom de la personne choisie, relu de la liste. */
export function PersonEchoProvider({ sections, answers, children }: { sections: readonly Section[]; answers: Readonly<Record<string, unknown>>; children: ReactNode }) {
  const services = useContext(AppServicesContext);
  const source = useContext(FormPeopleContext);
  const sources = echoSources(sections, answers);
  const signature = JSON.stringify(sources.map((item) => [item.entry.field.field_id, item.value, item.into]));
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    let alive = true;
    void Promise.all(
      sources.map(async (item): Promise<Array<[string, string]>> => {
        let people: readonly FormPerson[];
        if (source.preview) people = samplePeople(item.entry).people;
        else {
          const parameters = formPeopleParameters(source, item.entry.field.field_id);
          if (parameters === null || services === null) return [];
          try {
            people = (await loadFormPeople(services.client, parameters)).people;
          } catch {
            return [];
          }
        }
        const name = people.find((person) => person.id === item.value)?.display_name;
        return name === undefined ? [] : item.into.map((target): [string, string] => [target, name]);
      }),
    ).then((pairs) => {
      if (alive) setNames(new Map(pairs.flat()));
    });
    return () => {
      alive = false;
    };
    // La signature résume les champs repris et leurs valeurs.
  }, [signature, services, source.preview, source.workItemId, source.template?.artifact_id, source.on]);
  return <PersonEchoContext.Provider value={names}>{children}</PersonEchoContext.Provider>;
}

/** Sous-titre repris d'un champ ou d'une partie (« Vétérinaire · Claire Martin »), ou rien. */
export function EchoName({ target }: { target: string }) {
  const name = useContext(PersonEchoContext).get(target);
  if (name === undefined) return null;
  return (
    <span className="ay-fv2-echo" data-testid={`echo-${target}`}>
      {name}
    </span>
  );
}
